/**
 * Scenery placement — where every object in the world goes.
 *
 * The placement policy, in one paragraph. Props used to be decoration the
 * player walked through, so everything had to hug a wall to avoid being a lie
 * about the space; now anything with a `footprint` is a real obstacle, and the
 * good spots opened up. A bathtub can stand alone in the middle of an empty
 * office because the player is stopped by it. Density stays low and
 * deliberately uneven — a two-octave noise field decides which parts of the
 * level were lived in, and most of the map sits near zero — so the rooms that
 * do hold something stand out against long stretches of nothing. The point is
 * still navigation rather than decoration: an empty grid of identical rooms is
 * a random walk, whereas the room with the bathtub in it is a room you have
 * been in before.
 *
 * The tone is the constraint. Everything here is mundane and out of place
 * rather than spooky, presented without comment and at the finish of a cheap
 * commercial fit-out. A bathtub plumbed into nothing in an office is right.
 * Anything that would get a laugh is wrong.
 *
 * Five passes, in this order:
 *
 *  1. **Landmarks.** The things a previous survivor left: graffiti, tallies,
 *     arrows, signs and — once or twice a level — a camp. Rare, and placed
 *     structurally rather than randomly: at forks, at thresholds into big
 *     rooms, near exits, and along the route between the spawn and an exit.
 *     They run first so they get first pick of those spots.
 *  2. **Architecture.** The building being wrong: a waist-high wall stub
 *     crossing half a room, a doorframe standing on its own, three steps up to
 *     a blank wall. Second because these are the largest things placed and the
 *     hardest to fit, and because everything after them should read as having
 *     arrived in a room that was already like this.
 *  3. **Oddities.** One object where there should be none, rolled per room.
 *     Most rooms draw nothing; a few draw one thing; rarer still, a room comes
 *     up crammed with nine of the same thing in rows.
 *  4. **Clusters.** Pockets of ordinary junk, one theme per pocket so a pile
 *     reads as somebody's leftover rather than as a random draw. These are the
 *     texture the oddities are read against — too much of it and the bathtub
 *     is just more furniture.
 *  5. **Dressing.** Ceiling stains over damp carpet, float rings on the water,
 *     pool ladders at the coping — the placements that follow from what the
 *     grid already says about a cell.
 *
 * Three conventions the renderer needs to know about:
 *
 *  - `yaw` faces **out of** the wall the prop leans on, so `forwardFromYaw`
 *    points into the room. The exceptions are `pool-ladder`, which faces the
 *    water it descends into, and free-standing floor debris, which is jittered.
 *  - A free-standing prop is squared up to the building, not spun at random:
 *    the fit-out was fitted by someone with a tape measure even where the plan
 *    made no sense.
 *  - For `arrow`, `variant & 1` is the side the arrow points: 0 for the prop's
 *    left, 1 for its right, in the plane of the wall it is painted on. It is
 *    chosen so following it takes you toward an exit.
 */

import { TUNING } from '../../core/config';
import { UNREACHABLE } from '../../core/grid';
import { clamp } from '../../core/math';
import {
  Cell,
  type ExitPoint,
  type Grid,
  type ItemSpot,
  type LevelSpec,
  type PropKind,
  type PropSpot,
  type RNG,
  type Region,
  type Vec2,
} from '../../core/types';
import {
  analyzeSites,
  DIR_DX,
  DIR_DY,
  DIR_OPPOSITE,
  isCorner,
  isThroughCorridor,
  type PropSites,
} from './propSites';
import { cellsAcceptable, footprintCells, passageSurvives } from './propSolid';
import {
  CAMP_KIT,
  DENSITY,
  PROP_DEFS,
  THEMES,
  type DensityProfile,
  type PropDef,
  type PropTheme,
  type PropWeight,
} from './propTables';

export interface PropContext {
  spec: LevelSpec;
  grid: Grid;
  regions: Region[];
  rng: RNG;
  /** Row-major BFS distance in cells from the spawn; `0xffff` is unreachable. */
  distanceFromSpawn: Uint16Array;
  /**
   * The real exits, as placed. Landmarks are the only signposting the game has,
   * so signs and arrows are aimed at these rather than at a guess: a sign that
   * points two rooms wide of the way out is worse than no sign.
   */
  exits: readonly ExitPoint[];
  /**
   * The supply spots, as placed. Optional only so this module keeps compiling
   * against a caller that predates solid props; pass them. Without them a
   * bathtub can end up standing on a granola bar, which is the one collision
   * outcome the player is entitled to be annoyed by.
   */
  items?: readonly ItemSpot[];
}

/** Metres between a floor prop's centre and the wall it backs onto. */
const FLOOR_BACK = 0.5;
/** Metres between a wall-mounted prop and the wall surface. */
const WALL_BACK = 0.08;
/** Metres between flat floor debris and the wall. It spills further out. */
const FLAT_BACK = 0.78;
/**
 * Metres of air between a *solid* floor prop's collision box and the wall.
 * These are set back by their own half-depth instead of a fixed amount, so the
 * box lands where the object looks like it is rather than half inside the wall.
 */
const SOLID_BACK = 0.05;
/** Corner props pull off both walls by this fraction of the usual set-back. */
const CORNER_SCALE = 0.72;
/** Clearance kept under the ceiling for wall-mounted props. */
const HEAD_ROOM = 0.35;
/** The four square headings, for props that line up with the building. */
const QUARTER_TURNS = [0, Math.PI / 2, Math.PI, -Math.PI / 2] as const;
/** Reused by every footprint test, so the hot path allocates nothing. */
const FP_CELLS: number[] = [];

/**
 * Place every prop on the level. Deterministic in `ctx.rng`: the same seed
 * produces the same scenery, in the same order, with the same ids.
 */
export function placeProps(ctx: PropContext): PropSpot[] {
  const { spec, grid, regions, rng, distanceFromSpawn, exits } = ctx;
  const sites = analyzeSites(spec, grid, regions, rng, distanceFromSpawn, exits, ctx.items);
  const out: PropSpot[] = [];

  placeLandmarks(sites, rng, out);
  placeArchitecture(sites, rng, out);
  placeOddities(sites, rng, out);
  placeClusters(sites, rng, out);
  placeDressing(sites, rng, out);

  // Ids are handed out at the end so the passes above can be reordered or
  // extended without any of them having to agree on a counter.
  for (let i = 0; i < out.length; i++) out[i].id = i;
  return out;
}

/* ------------------------------------------------------------------ *
 * Emitting one prop
 * ------------------------------------------------------------------ */

interface EmitOpts {
  /** Wall/anchor direction index, or -1 for a free-standing spot. */
  dir: number;
  /** Second wall for corner props, or -1. */
  perp?: number;
  /** Metres of jitter along the wall. Runs pass 0 so they line up. */
  lateral?: number;
  /** Face toward `dir` rather than away from it. Used by pool ladders. */
  faceInto?: boolean;
  /** Override the variant. Used by arrows to carry their pointing side. */
  variant?: number;
  /**
   * Fixed mount height and scale. A run draws these once and hands the same
   * pair to every segment: four lengths of pipe each at its own height climb
   * the wall like a staircase, and a rack of shelving with three different
   * heights is three units that happen to be touching.
   */
  height?: number;
  scale?: number;
  /** Force the facing outright, for anything that has to line up with something. */
  yaw?: number;
  /**
   * Sit exactly on the cell centre plus `offset`, with none of the usual
   * jitter. Anything meant to read as installed rather than dropped — a row of
   * desks, a soffit crossing a room in three pieces — needs this, because a
   * few centimetres of noise is the difference between a fit-out and a mess.
   */
  exact?: boolean;
  /**
   * Metres of offset from the cell centre, applied before the wall set-back.
   * A wall stub is anchored to the cell whose wall it grows out of but is three
   * metres long, so its centre is nowhere near that cell's middle.
   */
  offset?: Vec2;
}

/**
 * Build one `PropSpot`, append it and mark its cell used. Returns false when
 * the spot turned out to be unusable, which only happens for solid props: the
 * footprint is checked against the grid and against everything solid already
 * placed, and a prop that would seal a route is refused. `id` is left at 0;
 * `placeProps` numbers the whole list once every pass has run.
 */
function emit(
  sites: PropSites,
  rng: RNG,
  out: PropSpot[],
  cell: number,
  kind: PropKind,
  opts: EmitOpts,
): boolean {
  const def = PROP_DEFS[kind];
  const { grid, spec } = sites;
  const x = cell % grid.w;
  const y = (cell / grid.w) | 0;
  const base = grid.cellToWorld(x, y);
  const half = grid.cellSize * 0.5;

  const dir = opts.dir;
  const perp = opts.perp ?? -1;
  const corner = perp >= 0;
  const scale = opts.scale ?? rng.range(def.scale[0], def.scale[1]);

  let back: number;
  if (def.mount === 'wall') back = half - WALL_BACK;
  else if (def.mount === 'floor-flat') back = half - FLAT_BACK;
  else if (def.mount === 'ceiling' || def.mount === 'floor-free') back = 0;
  else if (def.footprint) back = half - (def.footprint.halfZ * scale + SOLID_BACK);
  else back = half - FLOOR_BACK;
  back = Math.max(0, back) * (corner ? CORNER_SCALE : 1);

  let px = base.x + (opts.offset?.x ?? 0);
  let pz = base.z + (opts.offset?.z ?? 0);
  let fx = 0;
  let fz = 0;
  if (dir >= 0) {
    const dx = DIR_DX[dir];
    const dz = DIR_DY[dir];
    px += dx * back;
    pz += dz * back;
    fx += dx;
    fz += dz;
    if (corner) {
      const qx = DIR_DX[perp];
      const qz = DIR_DY[perp];
      px += qx * back;
      pz += qz * back;
      fx += qx;
      fz += qz;
    } else {
      // Slide along the wall so a room's props are not all on cell centres.
      const lateral = opts.lateral ?? 0;
      if (lateral !== 0) {
        px += -dz * lateral;
        pz += dx * lateral;
      }
    }
  } else if (opts.exact) {
    // Left exactly where the caller put it.
  } else if (def.mount === 'floor-free') {
    // Nudged off the cell centre so a room of these is not visibly a grid, but
    // not far enough to move which cells the footprint claims.
    const jitter = Math.min(0.3, grid.cellSize * 0.09);
    px += rng.range(-jitter, jitter);
    pz += rng.range(-jitter, jitter);
  } else {
    const jitter = grid.cellSize * 0.16;
    px += rng.range(-jitter, jitter);
    pz += rng.range(-jitter, jitter);
  }

  // yaw = 0 looks down -Z, so a heading (vx, vz) is atan2(vx, -vz). Facing out
  // of the wall means negating the direction that points into it.
  let yaw: number;
  if (opts.yaw !== undefined) {
    yaw = opts.yaw;
  } else if (dir < 0) {
    // Free-standing things are square to the building; only loose debris spins.
    yaw =
      def.mount === 'floor-free'
        ? QUARTER_TURNS[rng.int(0, 3)] + rng.range(-0.09, 0.09)
        : rng.range(-Math.PI, Math.PI);
    if (def.mount === 'floor' || def.mount === 'floor-flat') yaw += rng.range(-0.14, 0.14);
  } else {
    yaw = opts.faceInto ? Math.atan2(fx, -fz) : Math.atan2(-fx, fz);
    if (def.mount === 'floor' || def.mount === 'floor-flat') yaw += rng.range(-0.14, 0.14);
  }

  let height = 0;
  if (opts.height !== undefined) {
    height = opts.height;
  } else if (def.mount === 'wall') {
    height = clamp(rng.range(def.height[0], def.height[1]), 0, spec.ceilingHeight - HEAD_ROOM);
  } else if (def.mount === 'ceiling') {
    height = spec.ceilingHeight;
  } else if (grid.cells[cell] === Cell.Water) {
    // Anything on the water sits at the surface, not on the pool floor.
    height = spec.waterDepth ?? 0;
  }

  const fp = def.footprint;
  // Sky check, repeated from `eligible` because `emit` is the one gate every
  // pass goes through and the oddity pass does not consult `eligible`. A 4.2 m
  // tree in a 3.4 m room grows out through the roof tiles.
  if (fp && fp.height > spec.ceilingHeight - HEAD_ROOM && grid.cells[cell] !== Cell.Open) return false;
  if (fp) {
    const claimed = footprintCells(sites, px, pz, yaw, fp, scale, FP_CELLS);
    if (!cellsAcceptable(sites, claimed)) return false;
    // Below the step-over height the player walks over it, so it cannot seal
    // anything; the box still exists for the entities to route around.
    if (fp.height >= TUNING.player.stepOverHeight && !passageSurvives(sites, claimed)) return false;
    for (const i of claimed) {
      if (!grid.isWalkable(i % grid.w, (i / grid.w) | 0)) continue;
      sites.blocked[i] = 1;
      sites.taken[i] = 1;
    }
  } else if (def.mount === 'floor-free') {
    // The doorframe has no box, but it still fills its cell as far as anything
    // else is concerned: a column cluster growing through the middle of one
    // would be the sort of accident that reads as a bug rather than as design.
    sites.blocked[cell] = 1;
  }

  sites.taken[cell] = 1;
  out.push({
    id: 0,
    pos: { x: px, z: pz },
    yaw,
    kind,
    height,
    scale,
    variant: opts.variant ?? rng.int(0, 0xffff),
    // Shared with the def rather than copied: nothing mutates a footprint, and
    // a level carries a few hundred props.
    ...(fp ? { footprint: fp } : {}),
  });
  return true;
}

/* ------------------------------------------------------------------ *
 * Choosing a wall
 * ------------------------------------------------------------------ */

/** True when this cell can take this kind at all. */
function eligible(sites: PropSites, cell: number, def: PropDef): boolean {
  if (sites.taken[cell]) return false;
  if (def.mount === 'floor-free') {
    if (sites.freeAt[cell] < 0) return false;
  } else if (sites.hostAt[cell] < 0) {
    return false;
  }
  // Cheap rejection before the footprint maths. The real test is in `emit`.
  if (def.footprint && (sites.reserved[cell] || sites.blocked[cell])) return false;
  // Anything taller than the level's ceiling needs sky over it. `Cell.Open` is
  // the hole in the roof; everywhere else a 4 m tree grows through somebody's
  // front room and out of the tiles. The renderer lets these keep their authored
  // size (`Anchor.unroofed`) precisely because the placer is what decides where
  // they may stand.
  if (def.footprint && def.footprint.height > sites.spec.ceilingHeight - HEAD_ROOM) {
    if (sites.grid.cells[cell] !== Cell.Open) return false;
  }
  const mask = sites.solidMask[cell];
  if (def.wall && mask === 0) return false;
  if (def.bulky) {
    // A threshold and a length of one-cell corridor are both places the player
    // has to pass through. A dead end is not, so bulky things are welcome there.
    if (sites.nearDoor[cell]) return false;
    if (isThroughCorridor(mask)) return false;
  }
  return true;
}

/**
 * Pick which wall of a cell a prop leans on. Walls with open floor in front of
 * them win: a prop nobody can see from the room is a prop that did not happen.
 */
function chooseWall(sites: PropSites, rng: RNG, cell: number, def: PropDef): { dir: number; perp: number } | null {
  const mask = sites.solidMask[cell];
  if (mask === 0) return null;
  const grid = sites.grid;
  const x = cell % grid.w;
  const y = (cell / grid.w) | 0;

  const open: number[] = [];
  const all: number[] = [];
  for (let d = 0; d < 4; d++) {
    if ((mask & (1 << d)) === 0) continue;
    all.push(d);
    const o = DIR_OPPOSITE[d];
    if (grid.isWalkable(x + DIR_DX[o], y + DIR_DY[o])) open.push(d);
  }
  const pool = open.length > 0 ? open : all;
  const dir = pool[rng.int(0, pool.length - 1)];

  let perp = -1;
  if (def.corner && isCorner(mask)) {
    const wants = dir < 2 ? [2, 3] : [0, 1];
    const sides = wants.filter((d) => (mask & (1 << d)) !== 0);
    if (sides.length > 0) perp = sides[rng.int(0, sides.length - 1)];
  }
  return { dir, perp };
}

/**
 * Place one kind at one cell, extending it into a run along the wall where the
 * kind supports it. Returns how many props were emitted.
 */
function tryPlace(sites: PropSites, rng: RNG, out: PropSpot[], cell: number, kind: PropKind): number {
  const def = PROP_DEFS[kind];
  if (!eligible(sites, cell, def)) return 0;
  const wall = chooseWall(sites, rng, cell, def);
  if (def.wall && !wall) return 0;

  const dir = wall ? wall.dir : -1;
  const perp = wall ? wall.perp : -1;
  const runMax = def.run[1];
  // A run normally walks along the wall the first one leans on. A free-standing
  // kind has no wall, and until now that made `run` a dead letter for every one
  // of them — a line of crawl tubes or a row of hedges is exactly the thing that
  // reads as one object rather than as scattered clutter, so they run along a
  // grid axis instead.
  const freeRun = dir < 0 && def.mount === 'floor-free';
  const runLen = runMax > 1 && perp < 0 && (dir >= 0 || freeRun) ? rng.int(def.run[0], runMax) : 1;

  if (runLen <= 1) {
    const lateral = perp >= 0 ? 0 : rng.range(-0.3, 0.3) * (sites.grid.cellSize / 3);
    return emit(sites, rng, out, cell, kind, { dir, perp, lateral }) ? 1 : 0;
  }

  // A run walks along the wall it started on. It stops the moment the wall does
  // — a shelving unit that carries on past the end of the wall is worse than a
  // short one.
  const along = freeRun ? rng.int(0, 3) : dir < 2 ? (rng.chance(0.5) ? 2 : 3) : rng.chance(0.5) ? 0 : 1;
  const grid = sites.grid;
  const runHeight =
    def.mount === 'wall'
      ? clamp(rng.range(def.height[0], def.height[1]), 0, sites.spec.ceilingHeight - HEAD_ROOM)
      : 0;
  const runScale = rng.range(def.scale[0], def.scale[1]);
  // Everything in a free-standing run shares one heading, and that heading puts
  // the kind's long side along the line of the run. Left to the dice each piece
  // would face its own way, and four hedges pointing four ways is a bag of
  // hedges rather than a hedge.
  const runYaw = freeRun ? runHeading(def, along) : undefined;
  let x = cell % grid.w;
  let y = (cell / grid.w) | 0;
  let placed = 0;
  for (let k = 0; k < runLen; k++) {
    if (!grid.inBounds(x, y)) break;
    const idx = y * grid.w + x;
    if (dir >= 0 && (sites.solidMask[idx] & (1 << dir)) === 0) break;
    if (!eligible(sites, idx, def)) break;
    const ok = emit(sites, rng, out, idx, kind, {
      dir,
      perp: -1,
      lateral: 0,
      height: runHeight,
      scale: runScale,
      ...(runYaw === undefined ? {} : { yaw: runYaw }),
    });
    // A run that cannot continue stops rather than skipping a gap: a rack with
    // a unit missing from the middle of it is two racks.
    if (!ok) break;
    placed++;
    x += DIR_DX[along];
    y += DIR_DY[along];
  }
  return placed;
}

/**
 * The yaw that lays a kind's long side along a run heading. `along` is a grid
 * direction; the model's long local axis is X when its box is wider than it is
 * deep, and Z otherwise.
 */
function runHeading(def: PropDef, along: number): number {
  const fp = def.footprint;
  const longIsX = !fp || fp.halfX >= fp.halfZ;
  const alongX = along < 2;
  return longIsX === alongX ? 0 : Math.PI / 2;
}

/* ------------------------------------------------------------------ *
 * Clusters
 * ------------------------------------------------------------------ */

/**
 * Pockets of ordinary scenery. Seeds are drawn from the weight field, which
 * already folds in the lived-in noise, the distance from spawn and the region
 * kind, so most of the level never comes up at all.
 */
function placeClusters(sites: PropSites, rng: RNG, out: PropSpot[]): void {
  const profile = DENSITY[sites.spec.generator];
  const themes = THEMES[sites.spec.generator];
  // A level may legitimately have no scenery catalogue at all — Level 6 is
  // bare concrete and that is its entire premise. `pickTheme` would return
  // undefined off an empty table, so the emptiness is answered here instead.
  if (themes.length === 0) return;
  const target = Math.round(sites.walkableCount / profile.cellsPerCluster);

  for (let n = 0; n < target; n++) {
    const hostIdx = drawWeighted(sites, rng);
    if (hostIdx < 0) break;
    const cell = sites.hosts[hostIdx];
    const theme = pickTheme(rng, themes);
    const size = clusterSize(rng, profile.clusterMax);
    placeCluster(sites, rng, out, cell, theme, size);
    dampAround(sites, cell, profile.spacing);
  }
}

/**
 * Cluster sizes lean small: one chair in a room is memorable, eight is a
 * warehouse. Two or three is the sweet spot — enough that the room reads as
 * somebody's rather than as a spawn point, few enough to take in at a glance.
 */
function clusterSize(rng: RNG, max: number): number {
  const r = rng.next();
  const n = r < 0.28 ? 1 : r < 0.63 ? 2 : r < 0.88 ? 3 : 4;
  return Math.min(n, max);
}

function pickTheme(rng: RNG, themes: readonly PropTheme[]): PropTheme {
  let total = 0;
  for (const t of themes) total += t.weight;
  let r = rng.next() * total;
  for (const t of themes) {
    r -= t.weight;
    if (r <= 0) return t;
  }
  return themes[themes.length - 1];
}

function placeCluster(
  sites: PropSites,
  rng: RNG,
  out: PropSpot[],
  cell: number,
  theme: PropTheme,
  size: number,
): void {
  // Seeds are drawn from the host pool, which is every cell with a wall beside
  // it. A `floor-free` kind wants the opposite — all four neighbours walkable —
  // so on a level whose leads are all free-standing (a suburb of lawns, a
  // soft-play hall) the pools are disjoint by construction and every lead falls
  // through to its extras. Walk the seed out to the nearest clear cell instead:
  // the pocket still happens where the weight field asked for it, a couple of
  // metres off the wall, which is where a climbing frame or a parked car stands
  // anyway.
  const seed = seedFor(sites, rng, cell, PROP_DEFS[theme.lead]);
  let placed = tryPlace(sites, rng, out, seed, theme.lead);
  if (placed === 0) {
    // The lead did not fit — a cabinet in a one-cell corridor, say. Fall back to
    // the theme's smaller props so the pocket still happens.
    for (const kind of theme.extras) {
      const alt = seedFor(sites, rng, cell, PROP_DEFS[kind]);
      placed = tryPlace(sites, rng, out, alt, kind);
      if (placed > 0) break;
    }
  }
  if (placed === 0) return;
  if (placed >= size) return;

  for (const near of nearbySites(sites, rng, seed, 2)) {
    if (placed >= size) break;
    placed += tryPlace(sites, rng, out, near, rng.pick(theme.extras));
  }
}

/**
 * The cell a kind should actually be tried at, given a cluster seed drawn from
 * the host pool. Everything but `floor-free` is placed where it was asked for;
 * a free-standing kind steps out to the nearest untaken clear cell in the same
 * region, and stays put if there is none.
 */
function seedFor(sites: PropSites, rng: RNG, cell: number, def: PropDef): number {
  if (def.mount !== 'floor-free' || sites.freeAt[cell] >= 0) return cell;
  const grid = sites.grid;
  const cx = cell % grid.w;
  const cy = (cell / grid.w) | 0;
  const region = sites.regionAt[cell];
  let best = -1;
  let bestDist = Infinity;
  for (let dy = -FREE_REACH; dy <= FREE_REACH; dy++) {
    for (let dx = -FREE_REACH; dx <= FREE_REACH; dx++) {
      const x = cx + dx;
      const y = cy + dy;
      if (!grid.inBounds(x, y)) continue;
      const idx = y * grid.w + x;
      if (sites.freeAt[idx] < 0 || sites.taken[idx]) continue;
      if (sites.regionAt[idx] !== region) continue;
      // Ties broken by the dice rather than by scan order, so a room does not
      // fill from its top-left corner.
      const d = dx * dx + dy * dy + rng.next() * 0.5;
      if (d < bestDist) {
        bestDist = d;
        best = idx;
      }
    }
  }
  return best >= 0 ? best : cell;
}

/** How far a free-standing lead may walk off its seed to find clear floor. */
const FREE_REACH = 3;

/**
 * Untaken cells a prop could stand in within a Chebyshev radius, in a
 * deterministic shuffled order. Confined to the seed's own region: two cells
 * either side of a one-cell wall are two cells apart and in different rooms,
 * and a pocket that leaks through the wall is two half-pockets rather than one
 * whole one. Both pools are offered — `eligible` is what decides which of them
 * a given kind can use, and a pocket of free-standing scenery has to be able to
 * grow across open floor.
 */
function nearbySites(sites: PropSites, rng: RNG, cell: number, radius: number): number[] {
  const grid = sites.grid;
  const cx = cell % grid.w;
  const cy = (cell / grid.w) | 0;
  const region = sites.regionAt[cell];
  const out: number[] = [];
  for (let dy = -radius; dy <= radius; dy++) {
    for (let dx = -radius; dx <= radius; dx++) {
      if (dx === 0 && dy === 0) continue;
      const x = cx + dx;
      const y = cy + dy;
      if (!grid.inBounds(x, y)) continue;
      const idx = y * grid.w + x;
      if (sites.hostAt[idx] < 0 && sites.freeAt[idx] < 0) continue;
      if (sites.taken[idx]) continue;
      if (sites.regionAt[idx] !== region) continue;
      out.push(idx);
    }
  }
  return rng.shuffle(out);
}

/** Weighted draw over the host pool. Returns an index into `sites.hosts`, or -1. */
function drawWeighted(sites: PropSites, rng: RNG): number {
  const weight = sites.weight;
  let total = 0;
  for (let k = 0; k < weight.length; k++) total += weight[k];
  if (total <= 0) return -1;
  let r = rng.next() * total;
  for (let k = 0; k < weight.length; k++) {
    r -= weight[k];
    if (r <= 0) return k;
  }
  return weight.length - 1;
}

/** Crush the weight field around a placed cluster so the next one lands elsewhere. */
function dampAround(sites: PropSites, cell: number, radius: number): void {
  const grid = sites.grid;
  const cx = cell % grid.w;
  const cy = (cell / grid.w) | 0;
  for (let dy = -radius; dy <= radius; dy++) {
    for (let dx = -radius; dx <= radius; dx++) {
      const x = cx + dx;
      const y = cy + dy;
      if (!grid.inBounds(x, y)) continue;
      const k = sites.hostAt[y * grid.w + x];
      if (k < 0) continue;
      const d = Math.max(Math.abs(dx), Math.abs(dy)) / (radius + 1);
      sites.weight[k] *= d * d;
    }
  }
}

/* ------------------------------------------------------------------ *
 * Landmarks
 * ------------------------------------------------------------------ */

/**
 * The things somebody left behind. These are the only props that do real
 * navigational work, so they are placed against the structure of the level
 * rather than drawn from the density field, and there are only ever a handful
 * of them.
 */
function placeLandmarks(sites: PropSites, rng: RNG, out: PropSpot[]): void {
  const budget = DENSITY[sites.spec.generator].landmarks;

  // Camps first: they are the rarest and the fussiest about where they go.
  placeCamps(sites, rng, out, count(rng, budget.camp));

  // Signs are the only directions the game ever gives, so the first ones are
  // reserved outright for the ways out. Scoring alone will not do it: the cells
  // within sight of an exit are a fraction of a percent of the level, and a
  // weighted draw over the whole map lands in one about never.
  const signs = count(rng, budget.sign);
  const reserved = Math.min(signs, sites.exitCells.length);
  placeLandmarkKind(
    sites,
    rng,
    out,
    'sign',
    signs,
    14,
    (i, n) => (n < reserved ? exitSignScore(sites, i) : signScore(sites, i)),
    // The reserved ones have to *say* EXIT. Left to the dice a sign beside the
    // way down read `THIS IS NOT AN EXIT` as often as it read `EXIT`, which is
    // worse than no sign — the one piece of direction the game gives was
    // pointing the wrong way a sixth of the time. The renderer picks its atlas
    // row with `variant % rows` and keeps EXIT on row 0 (`SIGN_ROW.exit`), so
    // zero lands on it whatever the row count becomes. Not imported from
    // `render/propdecals.ts` on purpose: nothing under `world/` may depend on
    // the renderer, and zero satisfies the contract without the dependency.
    (n) => (n < reserved ? 0 : undefined),
  );

  placeLandmarkKind(sites, rng, out, 'arrow', count(rng, budget.arrow), 11, (i) => arrowScore(sites, i));
  placeLandmarkKind(sites, rng, out, 'tally-marks', count(rng, budget['tally-marks']), 12, (i) =>
    tallyScore(sites, i),
  );
  placeLandmarkKind(sites, rng, out, 'graffiti', count(rng, budget.graffiti), 9, (i) =>
    graffitiScore(sites, i),
  );
}

function count(rng: RNG, range: readonly [number, number]): number {
  return rng.int(range[0], range[1]);
}

function placeLandmarkKind(
  sites: PropSites,
  rng: RNG,
  out: PropSpot[],
  kind: PropKind,
  wanted: number,
  minSep: number,
  score: (cell: number, n: number) => number,
  /** Forces the variant of the nth one placed. Undefined leaves it to the dice. */
  variantFor?: (n: number) => number | undefined,
): void {
  const placed: number[] = [];
  for (let n = 0; n < wanted; n++) {
    const cell = drawScored(sites, rng, (cellIdx) => score(cellIdx, n), placed, minSep);
    // `continue`, not `break`: a score that came up empty for this draw may be
    // a different score on the next one. Signs switch tables partway through.
    if (cell < 0) continue;
    const wall = chooseWall(sites, rng, cell, PROP_DEFS[kind]);
    if (!wall) continue;
    const forced = variantFor?.(n);
    const opts: EmitOpts =
      kind === 'arrow'
        ? { dir: wall.dir, perp: -1, lateral: 0, variant: arrowVariant(sites, rng, cell, wall.dir) }
        : { dir: wall.dir, perp: -1, lateral: 0, variant: forced };
    if (!emit(sites, rng, out, cell, kind, opts)) continue;
    placed.push(cell);
    // Landmarks pull the ordinary junk away from themselves a little, so a sign
    // is read against a bare wall rather than from behind a stack of boxes.
    dampAround(sites, cell, 2);
  }
}

/**
 * Weighted draw over the host pool using an ad-hoc score, honouring a minimum
 * separation from the cells this kind has already claimed.
 */
function drawScored(
  sites: PropSites,
  rng: RNG,
  score: (cell: number) => number,
  placed: readonly number[],
  minSep: number,
): number {
  return drawFrom(sites, rng, sites.hosts, sites.scratch, score, placed, minSep);
}

/** The same draw over the free-standing pool. */
function drawFree(
  sites: PropSites,
  rng: RNG,
  score: (cell: number) => number,
  placed: readonly number[],
  minSep: number,
): number {
  return drawFrom(sites, rng, sites.frees, sites.freeScratch, score, placed, minSep);
}

function drawFrom(
  sites: PropSites,
  rng: RNG,
  pool: Int32Array,
  scratch: Float32Array,
  score: (cell: number) => number,
  placed: readonly number[],
  minSep: number,
): number {
  const { grid } = sites;
  const hosts = pool;
  let total = 0;
  for (let k = 0; k < hosts.length; k++) {
    const cell = hosts[k];
    scratch[k] = 0;
    if (sites.taken[cell]) continue;
    let ok = true;
    const x = cell % grid.w;
    const y = (cell / grid.w) | 0;
    for (const p of placed) {
      const dx = x - (p % grid.w);
      const dy = y - ((p / grid.w) | 0);
      if (Math.max(Math.abs(dx), Math.abs(dy)) < minSep) {
        ok = false;
        break;
      }
    }
    if (!ok) continue;
    const s = score(cell);
    if (s <= 0) continue;
    scratch[k] = s;
    total += s;
  }
  if (total <= 0) return -1;
  let r = rng.next() * total;
  let last = -1;
  for (let k = 0; k < hosts.length; k++) {
    if (scratch[k] <= 0) continue;
    last = hosts[k];
    r -= scratch[k];
    if (r <= 0) return last;
  }
  // Only reachable through floating-point drift, and then the last positive
  // candidate is exactly the one the draw was about to land on.
  return last;
}

/** How strongly a cell sits on a shortest route out. 1 on the line, 0 well off it. */
function onRoute(sites: PropSites, cell: number): number {
  const slack = sites.routeSlack[cell];
  if (slack === UNREACHABLE) return 0;
  if (slack <= 2) return 1;
  if (slack >= 24) return 0;
  return 1 - (slack - 2) / 22;
}

/**
 * Within sight of a way out, and nowhere else. Zero off the exit neighbourhood
 * so the draw cannot wander: this is a reservation, not a preference.
 */
function exitSignScore(sites: PropSites, cell: number): number {
  const d = sites.exitDist[cell];
  if (d === UNREACHABLE || d > 10) return 0;
  let s = 1 + (10 - d);
  // The threshold you come through is as good a place as the exit itself.
  if (sites.nearDoor[cell]) s += 3;
  if (sites.nearJunction[cell]) s += 2;
  return s;
}

/** Every other sign: at the mouth of a big space, or on the way somewhere. */
function signScore(sites: PropSites, cell: number): number {
  let s = 0.15;
  if (sites.nearDoor[cell]) {
    const rid = sites.regionAt[cell];
    if (rid >= 0 && sites.regions[rid].w * sites.regions[rid].h >= 110) s += 2.2;
    else s += 0.6;
  }
  if (sites.nearJunction[cell]) s += 1.0;
  s += 1.6 * onRoute(sites, cell);
  return s * sites.landmarkQuiet(cell);
}

/** An arrow only earns its place at a fork, and is worth twice as much on the way out. */
function arrowScore(sites: PropSites, cell: number): number {
  let s = 0;
  if (sites.nearJunction[cell]) s += 3;
  if (sites.nearDoor[cell]) s += 1.4;
  const rid = sites.regionAt[cell];
  if (rid >= 0 && sites.regions[rid].kind === 'hall') s += 1;
  if (s <= 0) return 0;
  s *= 1 + 1.7 * onRoute(sites, cell);
  return s * sites.landmarkQuiet(cell);
}

/** Tally marks mean somebody sat here for a while. Dead ends and closets. */
function tallyScore(sites: PropSites, cell: number): number {
  let s = 0.2;
  if (sites.openCount[cell] <= 1) s += 3;
  const rid = sites.regionAt[cell];
  if (rid >= 0 && sites.regions[rid].kind === 'closet') s += 2.5;
  if (isCorner(sites.solidMask[cell])) s += 0.8;
  s *= 0.5 + 1.5 * (sites.dist[cell] / sites.maxDist);
  return s * sites.landmarkQuiet(cell);
}

/** Graffiti goes where people stop and look: forks, thresholds, corridors. */
function graffitiScore(sites: PropSites, cell: number): number {
  let s = 0.5;
  if (sites.nearJunction[cell]) s += 1.2;
  if (sites.nearDoor[cell]) s += 0.8;
  const rid = sites.regionAt[cell];
  if (rid >= 0) {
    const kind = sites.regions[rid].kind;
    if (kind === 'hall' || kind === 'warren') s += 0.6;
  }
  if (sites.openCount[cell] <= 1) s += 0.5;
  s *= 1 + 0.6 * onRoute(sites, cell);
  return s * sites.landmarkQuiet(cell);
}

/**
 * Which way the arrow points, packed into `variant`. Only two of the four
 * compass directions are drawable on a wall you are facing, so the choice is
 * between the arrow's own left and right; whichever of them gets closer to an
 * exit wins. Following one should be worth doing.
 */
function arrowVariant(sites: PropSites, rng: RNG, cell: number, dir: number): number {
  const grid = sites.grid;
  const along = dir < 2 ? [2, 3] : [0, 1];
  let best = along[rng.chance(0.5) ? 0 : 1];
  let bestGain = -Infinity;
  for (const a of along) {
    let x = cell % grid.w;
    let y = (cell / grid.w) | 0;
    // The best cell reachable this way, not the sixth one along: corridors bend,
    // and in a maze a straight line runs into a wall after two cells.
    let gain = -Infinity;
    for (let step = 0; step < 6; step++) {
      x += DIR_DX[a];
      y += DIR_DY[a];
      if (!grid.isWalkable(x, y)) break;
      const d = sites.exitDist[y * grid.w + x];
      if (d === UNREACHABLE) break;
      const g = sites.exitDist[cell] - d;
      if (g > gain) gain = g;
    }
    if (gain > bestGain) {
      bestGain = gain;
      best = a;
    }
  }
  // Facing out of the wall, "right" is the direction 90 degrees clockwise from
  // it: for a facing of (-dx, -dz) that is (dz, -dx).
  const rightX = DIR_DY[dir];
  const rightZ = -DIR_DX[dir];
  const side = DIR_DX[best] * rightX + DIR_DY[best] * rightZ > 0 ? 1 : 0;
  return (rng.int(0, 0x3fff) << 1) | side;
}

/**
 * Somebody's abandoned sleeping spot. One or two a level, in a dead end, deep
 * in, with their rubbish around them. Dead ends and closets are also where the
 * supply pass hides most of its caches, so a camp usually turns out to be worth
 * the walk — which is the point of finding one.
 */
function placeCamps(sites: PropSites, rng: RNG, out: PropSpot[], wanted: number): void {
  const placed: number[] = [];
  for (let n = 0; n < wanted; n++) {
    const cell = drawScored(sites, rng, (i) => campScore(sites, i), placed, 26);
    if (cell < 0) break;
    const wall = chooseWall(sites, rng, cell, PROP_DEFS.camp);
    if (!wall) continue;
    if (!emit(sites, rng, out, cell, 'camp', { dir: wall.dir, perp: wall.perp, lateral: 0 })) continue;
    placed.push(cell);

    // The kit around it, plus tallies on the wall: somebody was here a while.
    const kit = CAMP_KIT[sites.spec.generator];
    const near = nearbySites(sites, rng, cell, 2);
    let extras = rng.int(1, 2);
    for (const c of near) {
      if (extras <= 0) break;
      extras -= tryPlace(sites, rng, out, c, rng.pick(kit)) > 0 ? 1 : 0;
    }
    for (const c of near) {
      if (sites.taken[c]) continue;
      const w = chooseWall(sites, rng, c, PROP_DEFS['tally-marks']);
      if (!w) continue;
      if (emit(sites, rng, out, c, 'tally-marks', { dir: w.dir, perp: -1, lateral: 0 })) break;
    }
    dampAround(sites, cell, 4);
  }
}

function campScore(sites: PropSites, cell: number): number {
  const d = sites.dist[cell];
  if (d === UNREACHABLE || d < sites.maxDist * 0.35) return 0;
  let s = 0;
  if (sites.openCount[cell] <= 1) s += 4;
  const rid = sites.regionAt[cell];
  if (rid >= 0 && sites.regions[rid].kind === 'closet') s += 4;
  if (s <= 0) return 0;
  if (isCorner(sites.solidMask[cell])) s += 1;
  return s * (0.6 + 1.2 * (d / sites.maxDist));
}

/* ------------------------------------------------------------------ *
 * Architecture that does not make sense
 * ------------------------------------------------------------------ */

/**
 * Cells kept between two structural pieces. Generous: the effect depends on
 * each one being the only strange thing in sight, so that it reads as a
 * decision somebody made rather than as a style the building has.
 */
const ARCH_SEP = 9;

/**
 * The building being wrong.
 *
 * A room stops being a rectangle the moment there is a waist-high wall stub
 * running halfway across it, and this is by a distance the cheapest way to make
 * a floor plan feel abandoned mid-thought — cheaper than any amount of furniture
 * and cheaper than editing the grid, because a wall stub can end in mid-air and
 * a grid wall cannot.
 *
 * Every piece has to read as deliberate and pointless at the same time. So they
 * are square to the building, set out at plausible dimensions, and finished like
 * everything else; what is wrong is only ever that they stop, or that they lead
 * nowhere, or that there are three columns where one would do.
 */
function placeArchitecture(sites: PropSites, rng: RNG, out: PropSpot[]): void {
  const profile = DENSITY[sites.spec.generator];
  if (profile.architectureKinds.length === 0 || profile.architecturePer1k <= 0) return;
  const wanted = Math.round((sites.walkableCount / 1000) * profile.architecturePer1k);

  const placed: number[] = [];
  for (let n = 0; n < wanted; n++) {
    const kind = pickWeighted(rng, profile.architectureKinds);
    const cell = placePiece(sites, rng, out, kind, placed);
    if (cell < 0) continue;
    placed.push(cell);
    // Ordinary junk keeps its distance: a wall that stops in mid-air only reads
    // as deliberate if there is nothing stacked against it.
    dampAround(sites, cell, 3);
  }
}

/** Dispatch one structural piece to whichever siting rule suits it. */
function placePiece(sites: PropSites, rng: RNG, out: PropSpot[], kind: PropKind, placed: number[]): number {
  switch (kind) {
    case 'half-wall':
    case 'stub-wall':
      return placeWallStub(sites, rng, out, kind, placed);
    case 'free-doorframe':
      return placeFreeDoorframe(sites, rng, out, placed);
    case 'false-door':
    case 'inner-window':
      return placeBlindOpening(sites, rng, out, kind, placed);
    case 'soffit':
      return placeSoffit(sites, rng, out, placed);
    case 'partition':
      return placePartitionRow(sites, rng, out, placed);
    case 'stairs-nowhere':
      return placeWallBacked(sites, rng, out, kind, placed);
    default:
      return placeInTheOpen(sites, rng, out, kind, placed);
  }
}

/**
 * A wall that stops. Most of them grow out of a real wall and end in mid-air
 * partway across the room, which is what a builder who ran out of instructions
 * would leave; the rest start and finish in mid-air, which is worse and is
 * therefore rarer.
 */
function placeWallStub(sites: PropSites, rng: RNG, out: PropSpot[], kind: PropKind, placed: number[]): number {
  const def = PROP_DEFS[kind];
  const fp = def.footprint;
  if (!fp) return -1;
  const scale = rng.range(def.scale[0], def.scale[1]);

  if (!rng.chance(0.72)) {
    const cell = drawFree(sites, rng, (i) => openScore(sites, i), placed, ARCH_SEP);
    if (cell < 0) return -1;
    const yaw = QUARTER_TURNS[rng.int(0, 3)];
    return emit(sites, rng, out, cell, kind, { dir: -1, yaw, scale }) ? cell : -1;
  }

  const cell = drawScored(sites, rng, (i) => stubScore(sites, i), placed, ARCH_SEP);
  if (cell < 0) return -1;
  const wall = chooseWall(sites, rng, cell, def);
  if (!wall) return -1;

  // The stub runs out of the wall it is anchored to, so its far end lands
  // wherever its own length puts it: one end is built, the other simply stops.
  const half = sites.grid.cellSize * 0.5;
  const reach = half - fp.halfX * scale;
  const offset = { x: DIR_DX[wall.dir] * reach, z: DIR_DY[wall.dir] * reach };
  const yaw = Math.atan2(DIR_DY[wall.dir], DIR_DX[wall.dir]);
  return emit(sites, rng, out, cell, kind, { dir: -1, yaw, scale, offset, exact: true }) ? cell : -1;
}

/** A doorframe on its own, leading from nothing to nothing. */
function placeFreeDoorframe(sites: PropSites, rng: RNG, out: PropSpot[], placed: number[]): number {
  const inCorridor = rng.chance(0.38);
  const cell = inCorridor
    ? drawScored(sites, rng, (i) => corridorScore(sites, i), placed, ARCH_SEP)
    : drawFree(sites, rng, (i) => openScore(sites, i), placed, ARCH_SEP);
  if (cell < 0) return -1;
  // Square to the run it stands in, so it reads as a door for a wall that was
  // never built rather than as something that fell over.
  const yaw = inCorridor
    ? (sites.solidMask[cell] & 0b0011) !== 0
      ? 0
      : Math.PI / 2
    : QUARTER_TURNS[rng.int(0, 3)];
  return emit(sites, rng, out, cell, 'free-doorframe', { dir: -1, yaw, exact: true }) ? cell : -1;
}

/**
 * A door that has never opened, or a window into a room that is dark. Both want
 * solid rock on the far side: an interior window you can see a lit corridor
 * through is a window, and an interior window with nothing behind it is not.
 */
function placeBlindOpening(sites: PropSites, rng: RNG, out: PropSpot[], kind: PropKind, placed: number[]): number {
  const needsDark = kind === 'inner-window';
  const cell = drawScored(sites, rng, (i) => blindScore(sites, i, needsDark), placed, ARCH_SEP);
  if (cell < 0) return -1;
  const dir = blindWall(sites, rng, cell, needsDark);
  if (dir < 0) return -1;
  const lateral = rng.range(-0.3, 0.3) * (sites.grid.cellSize / 3);
  return emit(sites, rng, out, cell, kind, { dir, perp: -1, lateral }) ? cell : -1;
}

/** A bulkhead crossing the room in a straight line, boxing in nothing. */
function placeSoffit(sites: PropSites, rng: RNG, out: PropSpot[], placed: number[]): number {
  const cell = drawFree(sites, rng, (i) => openScore(sites, i), placed, ARCH_SEP);
  if (cell < 0) return -1;
  const grid = sites.grid;
  const def = PROP_DEFS.soffit;
  const axis = rng.chance(0.5) ? 0 : 2;
  const yaw = axis === 0 ? 0 : Math.PI / 2;
  const scale = rng.range(def.scale[0], def.scale[1]);
  const len = rng.int(2, 4);

  let x = (cell % grid.w) - DIR_DX[axis] * (len >> 1);
  let y = ((cell / grid.w) | 0) - DIR_DY[axis] * (len >> 1);
  let count = 0;
  for (let k = 0; k < len; k++) {
    if (!grid.inBounds(x, y)) break;
    const i = y * grid.w + x;
    // Nothing to hang it from where the ceiling is missing, and it should read
    // against a floor rather than across a wall.
    if (!grid.isWalkable(x, y) || grid.cells[i] === Cell.Open) break;
    if (!sites.taken[i] && emit(sites, rng, out, i, 'soffit', { dir: -1, yaw, scale, exact: true })) count++;
    x += DIR_DX[axis];
    y += DIR_DY[axis];
  }
  return count > 0 ? cell : -1;
}

/** A row of cubicle dividers, partitioning nothing off from nothing. */
function placePartitionRow(sites: PropSites, rng: RNG, out: PropSpot[], placed: number[]): number {
  const def = PROP_DEFS.partition;
  const cell = drawFree(sites, rng, (i) => openScore(sites, i), placed, ARCH_SEP);
  if (cell < 0) return -1;
  const grid = sites.grid;
  // The row steps one way and the panels face across it, which is how an
  // office is set out and why a run of them still reads as somebody's plan.
  const along = rng.chance(0.5) ? 0 : 2;
  const yaw = along === 0 ? Math.PI / 2 : 0;
  const scale = rng.range(def.scale[0], def.scale[1]);
  const wanted = rng.int(Math.max(1, def.run[0]), Math.max(1, def.run[1]));

  let x = cell % grid.w;
  let y = (cell / grid.w) | 0;
  let count = 0;
  for (let k = 0; k < wanted; k++) {
    if (!grid.inBounds(x, y)) break;
    const i = y * grid.w + x;
    if (!eligible(sites, i, def)) break;
    if (!emit(sites, rng, out, i, 'partition', { dir: -1, yaw, scale, exact: true })) break;
    count++;
    x += DIR_DX[along];
    y += DIR_DY[along];
  }
  return count > 0 ? cell : -1;
}

/** Anything that needs a blank wall behind it: three steps up to nothing. */
function placeWallBacked(sites: PropSites, rng: RNG, out: PropSpot[], kind: PropKind, placed: number[]): number {
  const cell = drawScored(sites, rng, (i) => stubScore(sites, i), placed, ARCH_SEP);
  if (cell < 0) return -1;
  return tryPlace(sites, rng, out, cell, kind) > 0 ? cell : -1;
}

/** Anything that just stands in the open: three columns set far too close. */
function placeInTheOpen(sites: PropSites, rng: RNG, out: PropSpot[], kind: PropKind, placed: number[]): number {
  const cell = drawFree(sites, rng, (i) => openScore(sites, i), placed, ARCH_SEP);
  if (cell < 0) return -1;
  return emit(sites, rng, out, cell, kind, { dir: -1 }) ? cell : -1;
}

/** Room enough in front of a wall to build something out from it. */
function stubScore(sites: PropSites, cell: number): number {
  if (sites.solidMask[cell] === 0) return 0;
  if (sites.nearDoor[cell] || sites.reserved[cell] || sites.blocked[cell]) return 0;
  if (sites.openness[cell] < 5) return 0;
  const rid = sites.regionAt[cell];
  let s = 1;
  if (rid >= 0) {
    const region = sites.regions[rid];
    const area = region.w * region.h;
    // A closet with a wall stub in it is a smaller closet, not a wrong one.
    if (area < 24 || region.kind === 'closet') return 0;
    s += Math.min(2, area / 120);
  }
  return s * sites.quiet(cell);
}

/** Clear floor in a space big enough that a thing standing in it is conspicuous. */
function openScore(sites: PropSites, cell: number): number {
  if (sites.reserved[cell] || sites.blocked[cell]) return 0;
  let s = 0.4 + 0.6 * (sites.openness[cell] / 8);
  const rid = sites.regionAt[cell];
  if (rid >= 0) {
    const region = sites.regions[rid];
    const area = region.w * region.h;
    if (area < 20) return 0;
    s += Math.min(2, area / 140);
  }
  return s * sites.quiet(cell);
}

/** A length of one-cell corridor, for the things that stand across one. */
function corridorScore(sites: PropSites, cell: number): number {
  if (!isThroughCorridor(sites.solidMask[cell])) return 0;
  if (sites.nearDoor[cell] || sites.reserved[cell] || sites.blocked[cell]) return 0;
  return sites.quiet(cell);
}

/** Somewhere a door or window could be cut, preferably into solid rock. */
function blindScore(sites: PropSites, cell: number, needsDark: boolean): number {
  if (sites.solidMask[cell] === 0 || sites.reserved[cell]) return 0;
  const dark = blindWall(sites, null, cell, true) >= 0;
  if (needsDark && !dark) return 0;
  let s = dark ? 1.2 : 0.3;
  // Beside a real door is the best place for one that is not: you have already
  // learned what a door in this building looks like by the time you try it.
  if (sites.nearDoor[cell]) s += 0.7;
  if (sites.openness[cell] >= 5) s += 0.5;
  return s * sites.quiet(cell);
}

/**
 * A wall of this cell with solid rock behind it, or -1. With `rng` omitted this
 * is a pure test, which is how `blindScore` asks the question before drawing.
 */
function blindWall(sites: PropSites, rng: RNG | null, cell: number, needsDark: boolean): number {
  const grid = sites.grid;
  const x = cell % grid.w;
  const y = (cell / grid.w) | 0;
  const mask = sites.solidMask[cell];
  const dark: number[] = [];
  const any: number[] = [];
  for (let d = 0; d < 4; d++) {
    if ((mask & (1 << d)) === 0) continue;
    any.push(d);
    if (grid.isSolid(x + DIR_DX[d] * 2, y + DIR_DY[d] * 2)) dark.push(d);
  }
  const pool = dark.length > 0 ? dark : needsDark ? [] : any;
  if (pool.length === 0) return -1;
  return rng ? pool[rng.int(0, pool.length - 1)]! : pool[0]!;
}

/* ------------------------------------------------------------------ *
 * Objects that should not be here
 * ------------------------------------------------------------------ */

/** Candidate cells in a space per extra roll. Bigger rooms get more chances. */
const ROLL_CELLS = 80;
/** Rolls one space may get, however big it is. */
const MAX_ROLLS = 12;
/** Cells between two oddities in the same space. */
const ODDITY_SEP = 5;
/** Hard ceiling on a crammed room, so an atrium does not eat the frame budget. */
const HOARD_MAX = 20;

/**
 * One object where there should be none.
 *
 * The roll is per space rather than per cell, because the unit of the effect is
 * a room: you walk in, and there is a bathtub, and there is nothing else. Seven
 * ordinary rooms in eight draw nothing at all here, which is what buys the
 * eighth one. And whatever lands is singular — one bathtub is unsettling, a
 * bathroom suite is a bathroom.
 *
 * The exception is the hoard, which is rare enough to be a story: a room
 * stacked with chairs, nine identical desks set out in rows. That is the other
 * half of the variance, and it is the reason opening the next door is worth
 * doing.
 */
function placeOddities(sites: PropSites, rng: RNG, out: PropSpot[]): void {
  const profile = DENSITY[sites.spec.generator];
  if (profile.oddityKinds.length === 0) return;

  const freeByRegion = new Map<number, number[]>();
  const hostByRegion = new Map<number, number[]>();
  bucketByRegion(sites, sites.frees, freeByRegion);
  bucketByRegion(sites, sites.hosts, hostByRegion);

  // Ascending ids then shuffled, so the order depends on the seed and not on
  // the iteration order of a Map.
  const ids: number[] = [];
  for (let rid = 0; rid < sites.regions.length; rid++) {
    if (freeByRegion.has(rid) || hostByRegion.has(rid)) ids.push(rid);
  }
  rng.shuffle(ids);

  for (const rid of ids) {
    const frees = freeByRegion.get(rid) ?? [];
    const hosts = hostByRegion.get(rid) ?? [];
    const region = sites.regions[rid];
    // A warehouse bay is twenty rooms wide; one object in it is one object in a
    // hundred metres of floor, which reads as nothing at all.
    const rolls = Math.min(MAX_ROLLS, 1 + Math.floor((frees.length + hosts.length) / ROLL_CELLS));
    const chosen: number[] = [];
    for (let n = 0; n < rolls; n++) {
      const roll = rng.next();
      if (roll < profile.hoardChance) {
        placeHoard(sites, rng, out, region, frees, profile);
        break; // A crammed room is the whole room.
      }
      if (roll >= profile.hoardChance + profile.oddityChance) continue;
      const cell = placeOddity(sites, rng, out, profile, frees, hosts, chosen);
      if (cell >= 0) {
        chosen.push(cell);
        dampAround(sites, cell, 2);
      }
    }
  }
}

function bucketByRegion(sites: PropSites, pool: Int32Array, out: Map<number, number[]>): void {
  for (let k = 0; k < pool.length; k++) {
    const cell = pool[k]!;
    const rid = sites.regionAt[cell];
    if (rid < 0) continue;
    let list = out.get(rid);
    if (!list) out.set(rid, (list = []));
    list.push(cell);
  }
}

/** Place exactly one out-of-place object in a space. Returns its cell, or -1. */
function placeOddity(
  sites: PropSites,
  rng: RNG,
  out: PropSpot[],
  profile: DensityProfile,
  frees: readonly number[],
  hosts: readonly number[],
  chosen: readonly number[],
): number {
  for (let attempt = 0; attempt < 3; attempt++) {
    const kind = pickWeighted(rng, profile.oddityKinds);
    const def = PROP_DEFS[kind];
    // Solid things that would ordinarily lean on a wall stand out in the open a
    // fair share of the time. A bookshelf against a wall is a bookshelf; the
    // same bookshelf alone in the middle of a bare floor is the whole pass.
    const wantsOpen =
      def.mount === 'floor-free' ||
      def.mount === 'ceiling' ||
      (def.footprint !== undefined && rng.chance(0.4));
    const pool = wantsOpen && frees.length > 0 ? frees : hosts;
    if (pool.length === 0) continue;
    const cell = pickSpot(sites, rng, pool, chosen);
    if (cell < 0) continue;

    if (def.mount === 'ceiling') {
      if (sites.grid.cells[cell] === Cell.Open) continue;
      if (emit(sites, rng, out, cell, kind, { dir: -1, exact: true })) return cell;
      continue;
    }
    if (def.mount === 'floor-free') {
      // On a level with no open floor — a one-cell maze — it still gets its
      // bathtub; the bathtub just ends up against a wall like everything else.
      const wall = sites.freeAt[cell] >= 0 ? null : chooseWall(sites, rng, cell, def);
      const opts: EmitOpts = wall
        ? { dir: wall.dir, perp: -1, lateral: 0 }
        : { dir: -1, yaw: oddityYaw(sites, rng, cell) };
      if (emit(sites, rng, out, cell, kind, opts)) return cell;
      continue;
    }
    if (sites.freeAt[cell] >= 0) {
      if (emit(sites, rng, out, cell, kind, { dir: -1, yaw: oddityYaw(sites, rng, cell) })) return cell;
      continue;
    }
    if (tryPlace(sites, rng, out, cell, kind) > 0) return cell;
  }
  return -1;
}

/**
 * Which way a lone object faces. Mostly square to the room, and now and then
 * turned to face the nearest wall — a desk pushed up against blank plaster,
 * sat at by nobody, is worth more than the same desk in the middle of the floor.
 */
function oddityYaw(sites: PropSites, rng: RNG, cell: number): number {
  if (!rng.chance(0.35)) return QUARTER_TURNS[rng.int(0, 3)] + rng.range(-0.09, 0.09);
  const grid = sites.grid;
  const x = cell % grid.w;
  const y = (cell / grid.w) | 0;
  let best = -1;
  let bestRun = 99;
  for (let d = 0; d < 4; d++) {
    for (let k = 1; k <= 6; k++) {
      if (!grid.isSolid(x + DIR_DX[d] * k, y + DIR_DY[d] * k)) continue;
      if (k < bestRun) {
        bestRun = k;
        best = d;
      }
      break;
    }
  }
  if (best < 0) return QUARTER_TURNS[rng.int(0, 3)];
  // yaw = 0 looks down -Z, so a heading (vx, vz) is atan2(vx, -vz).
  return Math.atan2(DIR_DX[best], -DIR_DY[best]) + rng.range(-0.06, 0.06);
}

/** Sample the pool for the most open cell going, keeping oddities apart. */
function pickSpot(sites: PropSites, rng: RNG, pool: readonly number[], chosen: readonly number[]): number {
  let best = -1;
  let bestScore = -1;
  for (let t = 0; t < 8; t++) {
    const cell = pool[rng.int(0, pool.length - 1)]!;
    if (sites.taken[cell] || sites.blocked[cell] || sites.reserved[cell]) continue;
    if (!spacedFrom(sites, cell, chosen, ODDITY_SEP)) continue;
    const s = sites.openness[cell] + rng.next();
    if (s > bestScore) {
      bestScore = s;
      best = cell;
    }
  }
  return best;
}

/**
 * The rare room that is full. One kind, one facing, one size, set out on every
 * other cell so the rows line up with the walls and there is always a lane
 * between them. Somebody stacked these. They did not drop them.
 */
function placeHoard(
  sites: PropSites,
  rng: RNG,
  out: PropSpot[],
  region: Region,
  frees: readonly number[],
  profile: DensityProfile,
): void {
  if (profile.hoardKinds.length === 0 || frees.length === 0) return;
  const kind = rng.pick(profile.hoardKinds);
  const def = PROP_DEFS[kind];
  const yaw = QUARTER_TURNS[rng.int(0, 3)] + rng.range(-0.02, 0.02);
  const scale = rng.range(def.scale[0], def.scale[1]);
  const grid = sites.grid;

  let count = 0;
  for (const cell of frees) {
    if (count >= HOARD_MAX) break;
    const x = cell % grid.w;
    const y = (cell / grid.w) | 0;
    if ((x - region.x) % 2 !== 0 || (y - region.y) % 2 !== 0) continue;
    if (sites.taken[cell] || sites.blocked[cell] || sites.reserved[cell]) continue;
    if (emit(sites, rng, out, cell, kind, { dir: -1, yaw, scale, exact: true })) count++;
  }
}

/** Weighted draw over one of the catalogue tables. */
function pickWeighted(rng: RNG, table: readonly PropWeight[]): PropKind {
  let total = 0;
  for (const t of table) total += t.weight;
  let r = rng.next() * total;
  for (const t of table) {
    r -= t.weight;
    if (r <= 0) return t.kind;
  }
  return table[table.length - 1]!.kind;
}

/* ------------------------------------------------------------------ *
 * Dressing
 * ------------------------------------------------------------------ */

/**
 * The placements the grid itself dictates: a stain on the ceiling above the
 * carpet the water ruined, a ring floating in a pool, a ladder at the coping.
 * These are cheap and they tie the props to the surfaces around them, which is
 * most of what stops scenery reading as sprinkled on.
 */
function placeDressing(sites: PropSites, rng: RNG, out: PropSpot[]): void {
  const profile = DENSITY[sites.spec.generator];
  const grid = sites.grid;

  const damp: number[] = [];
  const overhead: number[] = [];
  const poolEdge: number[] = [];
  const surface: number[] = [];

  for (let y = 1; y < grid.h - 1; y++) {
    for (let x = 1; x < grid.w - 1; x++) {
      const i = y * grid.w + x;
      const c = grid.cells[i] as Cell;
      if (c === Cell.Void || c === Cell.Wall) continue;
      if (sites.quiet(i) <= 0) continue;
      // Cell.Open is a hole in the ceiling: nothing to stain, nothing to hang
      // from. Neither list can contain one, but the intent is worth stating.
      if (c === Cell.Damp) damp.push(i);
      if (c === Cell.Floor && sites.openCount[i] >= 3) overhead.push(i);
      if (c === Cell.Water) {
        let edge = false;
        for (let d = 0; d < 4 && !edge; d++) {
          if (grid.at(x + DIR_DX[d], y + DIR_DY[d]) === Cell.Floor) edge = true;
        }
        if (edge) surface.push(i);
      } else if (c === Cell.Floor || c === Cell.Damp) {
        for (let d = 0; d < 4; d++) {
          if (grid.at(x + DIR_DX[d], y + DIR_DY[d]) === Cell.Water) {
            poolEdge.push(i * 4 + d);
            break;
          }
        }
      }
    }
  }

  const ceilingBudget = Math.round((sites.walkableCount / 1000) * profile.ceilingPer1k);
  // A level that hung decorations gives most of the ceiling over to them; one
  // that did not splits the budget the way it always did.
  const wantsPaper = profile.ceilingKinds.includes('streamers');
  const stains = profile.ceilingKinds.includes('ceiling-stain')
    ? Math.round(ceilingBudget * (wantsPaper ? 0.3 : 0.65))
    : 0;
  const paper = wantsPaper ? Math.round(ceilingBudget * 0.45) : 0;
  const lamps = profile.ceilingKinds.includes('hanging-light')
    ? Math.max(0, ceilingBudget - stains - paper)
    : 0;

  // Stains follow the damp, because that is where the water got in.
  scatter(sites, rng, out, damp, stains, 6, 'ceiling-stain');
  // Paper chains go where a room has ceiling on both sides to pin them to, and
  // spread out — two sets of streamers three metres apart is a pile of paper.
  scatter(sites, rng, out, overhead, paper, 9, 'streamers');
  scatter(sites, rng, out, overhead, lamps, 11, 'hanging-light');

  if (sites.spec.generator === 'poolrooms') {
    scatter(sites, rng, out, surface, rng.int(3, 7), 7, 'float-ring');
    // Ladders face the water they go down into, not out of the wall behind
    // them, so they cannot go through `scatter`.
    const edges = rng.shuffle(poolEdge.slice());
    const placed: number[] = [];
    let ladders = rng.int(4, 9);
    for (const packed of edges) {
      if (ladders <= 0) break;
      const cell = (packed / 4) | 0;
      if (sites.taken[cell] || !spacedFrom(sites, cell, placed, 8)) continue;
      const opts: EmitOpts = { dir: packed % 4, perp: -1, lateral: 0, faceInto: true };
      if (!emit(sites, rng, out, cell, 'pool-ladder', opts)) continue;
      placed.push(cell);
      ladders--;
    }
  }

  if (sites.spec.generator === 'pipes') {
    // Standing water in a service corridor. Flat, walkable-looking, and the one
    // thing down there that is not another identical wall.
    scatter(sites, rng, out, damp, Math.min(28, Math.round(damp.length * 0.05)), 4, 'puddle');
  }
}

/**
 * Place `wanted` of one kind from a candidate list, keeping them apart from each
 * other. Separation is per-kind on purpose: a stain directly above a stack of
 * boxes is fine, two stains three metres apart is a texture.
 */
function scatter(
  sites: PropSites,
  rng: RNG,
  out: PropSpot[],
  candidates: number[],
  wanted: number,
  minSep: number,
  kind: PropKind,
): void {
  if (wanted <= 0 || candidates.length === 0) return;
  const order = rng.shuffle(candidates.slice());
  const placed: number[] = [];
  let left = wanted;
  for (const cell of order) {
    if (left <= 0) break;
    if (sites.taken[cell]) continue;
    if (!spacedFrom(sites, cell, placed, minSep)) continue;
    if (!emit(sites, rng, out, cell, kind, { dir: -1 })) continue;
    placed.push(cell);
    left--;
  }
}

/** True when `cell` is at least `minSep` cells from everything in `placed`. */
function spacedFrom(sites: PropSites, cell: number, placed: readonly number[], minSep: number): boolean {
  const w = sites.grid.w;
  const x = cell % w;
  const y = (cell / w) | 0;
  for (const p of placed) {
    const dx = x - (p % w);
    const dy = y - ((p / w) | 0);
    if (Math.max(Math.abs(dx), Math.abs(dy)) < minSep) return false;
  }
  return true;
}
